import React, { useEffect, useState } from "react";
import { Camera, Plus, Trash2, X, Image as ImageIcon, Save } from "lucide-react";
import { api, formatApiError } from "../../lib/api";

const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const DAYS = [
  { v: "vendredi", label: "Vendredi 1 Mai" },
  { v: "samedi", label: "Samedi 2 Mai" },
  { v: "dimanche", label: "Dimanche 3 Mai" },
];

const GalleryPanel = () => {
  const [items, setItems] = useState([]);
  const [adding, setAdding] = useState(false);

  const refresh = () => {
    api.get("/gallery").then((r) => setItems(r.data)).catch(() => setItems([]));
  };

  useEffect(() => {
    refresh();
  }, []);

  const remove = async (id) => {
    if (!confirm("Supprimer cette photo ?")) return;
    try {
      await api.delete(`/admin/gallery/${id}`);
      refresh();
    } catch (e) {
      alert(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const grouped = DAYS.map((d) => ({
    ...d,
    photos: items.filter((p) => p.day === d.v),
  }));

  return (
    <div data-testid="admin-gallery-panel">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div>
          <span className="font-condensed uppercase tracking-[0.3em] text-xs text-[#E60012]">Souvenirs</span>
          <h2 className="mt-1 font-display text-4xl uppercase">Galerie photos</h2>
          <p className="mt-1 text-white/55 text-sm">Téléversez les photos par jour de tournoi.</p>
        </div>
        <button
          onClick={() => setAdding(true)}
          data-testid="add-photo-btn"
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#E60012] hover:bg-[#ff1a30] font-condensed uppercase tracking-[0.2em] text-xs clip-tab"
        >
          <Plus className="w-4 h-4" /> Ajouter photo
        </button>
      </div>

      {grouped.map((d) => (
        <div key={d.v} className="mb-8" data-testid={`gallery-admin-day-${d.v}`}>
          <div className="flex items-center gap-3 mb-3">
            <Camera className="w-4 h-4 text-[#E60012]" />
            <h3 className="font-display text-2xl uppercase">{d.label}</h3>
            <span className="text-xs text-white/50 font-condensed uppercase tracking-wider">({d.photos.length})</span>
          </div>
          {d.photos.length === 0 ? (
            <div className="text-white/40 text-sm font-condensed uppercase tracking-wider py-4 px-3 bg-black/30">
              Aucune photo
            </div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2">
              {d.photos.map((p) => (
                <div key={p.id} className="relative aspect-square bg-black border border-white/10 group" data-testid={`admin-photo-${p.id}`}>
                  <img src={p.image_base64} alt="" className="w-full h-full object-cover" />
                  <button
                    onClick={() => remove(p.id)}
                    data-testid={`del-photo-${p.id}`}
                    className="absolute top-1 right-1 w-7 h-7 bg-black/80 hover:bg-red-600 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                  {p.caption && (
                    <div className="absolute inset-x-0 bottom-0 p-1.5 bg-black/70 text-[10px] text-white/80 truncate">
                      {p.caption}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      ))}

      {adding && (
        <PhotoUploader
          onClose={() => setAdding(false)}
          onSaved={() => {
            setAdding(false);
            refresh();
          }}
        />
      )}
    </div>
  );
};

const PhotoUploader = ({ onClose, onSaved }) => {
  const [day, setDay] = useState("vendredi");
  const [caption, setCaption] = useState("");
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const onPick = async (e) => {
    const list = Array.from(e.target.files || []);
    const out = [];
    for (const f of list) {
      if (f.size > 5 * 1024 * 1024) {
        setError(`${f.name} trop volumineux (max 5 Mo)`);
        continue;
      }
      out.push({ uid: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`, name: f.name, b64: await fileToBase64(f) });
    }
    setFiles((prev) => [...prev, ...out]);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (files.length === 0) {
      setError("Choisissez au moins une photo.");
      return;
    }
    setUploading(true);
    setError("");
    try {
      for (const f of files) {
        await api.post("/admin/gallery", { day, image_base64: f.b64, caption });
      }
      onSaved();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm overflow-y-auto p-4 flex items-start justify-center" data-testid="photo-uploader-modal">
      <form onSubmit={submit} className="relative w-full max-w-lg my-10 bg-[#0f0f0f] border border-white/10 p-6">
        <button type="button" onClick={onClose} className="absolute top-3 right-3 w-9 h-9 bg-black/70 hover:bg-[#E60012] flex items-center justify-center" data-testid="photo-uploader-close">
          <X className="w-4 h-4" />
        </button>
        <span className="font-condensed uppercase tracking-[0.3em] text-xs text-[#E60012]">Téléverser</span>
        <h2 className="mt-1 font-display text-2xl uppercase">Ajouter des photos</h2>

        <div className="mt-5 space-y-4">
          <div>
            <span className="text-xs text-white/60 font-condensed uppercase tracking-wider block mb-1">Jour</span>
            <div className="flex gap-2 flex-wrap">
              {DAYS.map((d) => (
                <button
                  key={d.v}
                  type="button"
                  onClick={() => setDay(d.v)}
                  data-testid={`uploader-day-${d.v}`}
                  className={`px-3 py-2 text-xs font-condensed uppercase tracking-[0.15em] border ${
                    day === d.v ? "bg-[#E60012] text-white border-[#E60012]" : "border-white/15 text-white/70 hover:border-[#E60012]"
                  }`}
                >
                  {d.label}
                </button>
              ))}
            </div>
          </div>

          <label className="block">
            <span className="text-xs text-white/60 font-condensed uppercase tracking-wider">Légende (optionnel — appliquée à toutes)</span>
            <input
              type="text"
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              data-testid="uploader-caption"
              className="mt-1 w-full bg-black border border-white/10 px-3 py-2 outline-none focus:border-[#E60012]"
              placeholder="ex. Match #5 — Lions vs Expos"
            />
          </label>

          <label className="block">
            <span className="text-xs text-white/60 font-condensed uppercase tracking-wider block mb-1">Photos (jusqu'à 5 Mo chacune)</span>
            <div className="border border-dashed border-white/15 hover:border-[#E60012] p-4 text-center cursor-pointer">
              <input type="file" multiple accept="image/*" onChange={onPick} className="hidden" id="photo-multi" data-testid="uploader-files" />
              <label htmlFor="photo-multi" className="cursor-pointer inline-flex items-center gap-2 px-3 py-2 text-xs font-condensed uppercase tracking-[0.15em]">
                <ImageIcon className="w-3.5 h-3.5" /> Choisir des photos
              </label>
            </div>
            {files.length > 0 && (
              <div className="mt-3 grid grid-cols-4 gap-2">
                {files.map((f, i) => (
                  <div key={f.uid} className="relative aspect-square">
                    <img src={f.b64} alt="" className="w-full h-full object-cover border border-white/10" />
                    <button type="button" onClick={() => setFiles(files.filter((_, j) => j !== i))} className="absolute top-1 right-1 w-5 h-5 bg-black/80 hover:bg-red-600 flex items-center justify-center">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </label>
        </div>

        {error && <div className="mt-3 text-sm text-red-400">{error}</div>}

        <div className="mt-5 flex items-center gap-2">
          <button
            type="submit"
            disabled={uploading || files.length === 0}
            data-testid="uploader-save"
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#E60012] hover:bg-[#ff1a30] disabled:opacity-50 font-condensed uppercase tracking-[0.15em] text-xs"
          >
            <Save className="w-3.5 h-3.5" /> {uploading ? "Téléversement…" : `Téléverser (${files.length})`}
          </button>
          <button type="button" onClick={onClose} className="px-4 py-2 border border-white/15 hover:border-white/40 font-condensed uppercase tracking-[0.15em] text-xs">
            Annuler
          </button>
        </div>
      </form>
    </div>
  );
};

export default GalleryPanel;
